/**
 * App pop-outs — open any page in a new browser tab, a new window, or as a tab
 * in a tabbed window. URL format + the one helper every "Open in…" control calls.
 *
 * WHY A URL AND NOT A TAB ID. The main app keeps its current page in
 * localStorage.activeTab and never changes the URL, so a second browser tab
 * opened at "/" would just restore whatever the first one was showing (and the
 * two would fight over that key). A pop-out instead carries its whole state in
 * the URL, and App renders it without the sidebar shell. Pop-outs never write
 * the shared activeTab key (lib/popoutStorageShield.ts).
 *
 * Two kinds of pop-out page:
 *   page  one app page, chromeless (no sidebar, rail or top bar)
 *           web      /popout/page?tab=members
 *           desktop  index.html?popout=page&tab=members
 *   app   a tab strip; each tab is a `page` pop-out in a same-origin iframe
 *           web      /popout/app?tabs=members,tasks&active=tasks
 *           desktop  index.html?popout=app&tabs=...&active=...
 * ("/" itself is rewritten to the marketing page on Vercel, so the web forms
 * have to be paths; vercel.json's final catch-all serves index.html for them.
 * The desktop bundle only has index.html, and the same URL works against the
 * Vite dev server.)
 *
 * A TAB KEY is an app page (activeTab id) plus optional page parameters:
 *   members
 *   workspace-messaging~view=email
 *   workspace-messaging~view=channels;channel=12
 * Ids, parameter names and values are restricted to URL-safe characters, so a
 * key never needs escaping inside `tabs=` (commas separate tabs) and a parsed
 * key is always one we could have built.
 *
 * The messaging pop-out (/popout/messaging?tabs=inbox,mailbox,dms,channel:12)
 * that came first is now a special case: its URLs are rewritten to /popout/app
 * on load (migrateLegacyMessagingLocation) and openMessagingPopout maps its tab
 * kinds onto app tabs.
 */
import { config } from '@/src/config';

// ---------------------------------------------------------------- tab keys
export interface AppTabSpec {
  /** activeTab id of the page, e.g. "members". */
  tab: string;
  /** Page parameters, e.g. { view: 'email' } for workspace-messaging. */
  params?: Record<string, string>;
}

export interface AppPopoutState {
  tabs: AppTabSpec[];
  /** appTabKey() of the tab in front. Null only when `tabs` is empty. */
  active: string | null;
  /** How many tabs at the front are pinned (URL `pinned=`). Omitted / 0 = none. */
  pinned?: number;
}

export const APP_POPOUT_PATH = '/popout/app';
export const PAGE_POPOUT_PATH = '/popout/page';
/** Kept so links and windows from the first release still open. */
export const POPOUT_PATH = '/popout/messaging';
export const POPOUT_QUERY_FLAG = 'popout';
/** A tab strip wider than this is unusable, and it bounds what a URL can make us mount. */
export const MAX_POPOUT_TABS = 12;
/** macOS groups windows that share this identifier into one native tab bar. */
export const TABBING_ID = 'weavein';
export const APP_WINDOW_LABEL_PREFIX = 'app-';
/** Windows created by the first release; still recognised as tabbed windows. */
export const MESSAGING_WINDOW_LABEL_PREFIX = 'messaging-';
/** Desktop event: ask an existing tabbed window to add tabs (payload: AddTabsEvent). */
export const ADD_TABS_EVENT = 'popout://add-tabs';
/** localStorage: label of the tabbed desktop window that last had focus. */
export const LAST_DESKTOP_HOST_KEY = 'weavein:popout:last-host-label';

// An app `activeTab` key. Every id the sidebar and the command palette navigate
// to fits (lowercase, digits, '-', '_'); see navigablePages.test.
const TAB_ID_RE = /^[a-z0-9][a-z0-9_-]{0,63}$/;
const PARAM_NAME_RE = /^[a-z][a-z0-9_]{0,23}$/;
const PARAM_VALUE_RE = /^[A-Za-z0-9_.-]{1,64}$/;
const MAX_PARAMS = 4;

export function isValidTabId(tab: unknown): tab is string {
  return typeof tab === 'string' && TAB_ID_RE.test(tab);
}

function cleanParams(params: Record<string, string> | undefined): Record<string, string> | undefined | null {
  if (!params) return undefined;
  const names = Object.keys(params);
  if (!names.length) return undefined;
  if (names.length > MAX_PARAMS) return null;
  const out: Record<string, string> = {};
  for (const name of names.sort()) {
    const value = params[name];
    if (!PARAM_NAME_RE.test(name) || typeof value !== 'string' || !PARAM_VALUE_RE.test(value)) return null;
    out[name] = value;
  }
  return out;
}

/** A spec we would render, with parameters in canonical (sorted) order, or null. */
export function cleanAppTabSpec(spec: unknown): AppTabSpec | null {
  if (!spec || typeof spec !== 'object') return null;
  const { tab, params } = spec as { tab?: unknown; params?: unknown };
  if (!isValidTabId(tab)) return null;
  if (params !== undefined && (params === null || typeof params !== 'object' || Array.isArray(params))) return null;
  const clean = cleanParams(params as Record<string, string> | undefined);
  if (clean === null) return null;
  return clean ? { tab, params: clean } : { tab };
}

export function appTabKey(spec: AppTabSpec): string {
  const params = spec.params ? Object.keys(spec.params).sort() : [];
  if (!params.length) return spec.tab;
  return `${spec.tab}~${params.map((k) => `${k}=${spec.params![k]}`).join(';')}`;
}

/** Parse one tab key. Returns null for anything we would not have built. */
export function parseAppTabKey(raw: unknown): AppTabSpec | null {
  if (typeof raw !== 'string') return null;
  const value = raw.trim();
  if (!value || value.length > 256) return null;
  const tilde = value.indexOf('~');
  if (tilde < 0) return isValidTabId(value) ? { tab: value } : null;
  const tab = value.slice(0, tilde);
  const rest = value.slice(tilde + 1);
  if (!rest) return null;
  const params: Record<string, string> = {};
  for (const pair of rest.split(';')) {
    const eq = pair.indexOf('=');
    if (eq <= 0) return null;
    const name = pair.slice(0, eq);
    if (name in params) return null;
    params[name] = pair.slice(eq + 1);
  }
  return cleanAppTabSpec({ tab, params });
}

/** Dedupe by key, drop anything invalid, cap the count, and pick a valid active tab. */
export function normalizeAppPopoutState(tabs: unknown[], active?: string | null): AppPopoutState {
  const seen = new Set<string>();
  const out: AppTabSpec[] = [];
  for (const raw of tabs) {
    const spec = cleanAppTabSpec(raw);
    if (!spec) continue;
    const key = appTabKey(spec);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(spec);
    if (out.length >= MAX_POPOUT_TABS) break;
  }
  const activeKey = active && seen.has(active) ? active : out[0] ? appTabKey(out[0]) : null;
  return { tabs: out, active: activeKey };
}

/** Tab keys (as sent over postMessage / Tauri events) → a validated state. */
export function stateFromKeys(keys: unknown, active?: unknown): AppPopoutState {
  const list = Array.isArray(keys) ? keys.slice(0, MAX_POPOUT_TABS * 2) : [];
  const specs = list.map(parseAppTabKey).filter((s): s is AppTabSpec => s !== null);
  const activeKey = typeof active === 'string' ? parseAppTabKey(active) : null;
  return normalizeAppPopoutState(specs, activeKey ? appTabKey(activeKey) : null);
}

export function stateToKeys(state: AppPopoutState): { tabs: string[]; active: string | null } {
  const norm = normalizeAppPopoutState(state.tabs, state.active);
  return { tabs: norm.tabs.map(appTabKey), active: norm.active };
}

// ---------------------------------------------------------------- URLs
export function parseAppPopoutParams(search: string): AppPopoutState {
  const params = new URLSearchParams(search);
  const state = stateFromKeys((params.get('tabs') || '').split(','), params.get('active'));
  const raw = params.get('pinned');
  const pinned = raw && /^[0-9]{1,2}$/.test(raw) ? Math.min(Number(raw), state.tabs.length) : 0;
  return pinned > 0 ? { ...state, pinned } : state;
}

/** Query string WITHOUT the leading "?". Keys are URL-safe by construction. */
export function buildAppPopoutQuery(state: AppPopoutState, opts: { withFlag?: boolean } = {}): string {
  const norm = normalizeAppPopoutState(state.tabs, state.active);
  const parts: string[] = [];
  if (opts.withFlag) parts.push(`${POPOUT_QUERY_FLAG}=app`);
  parts.push(`tabs=${norm.tabs.map(appTabKey).join(',')}`);
  if (norm.active) parts.push(`active=${norm.active}`);
  const pinned = Math.min(Number.isInteger(state.pinned) ? (state.pinned as number) : 0, norm.tabs.length);
  if (pinned > 0) parts.push(`pinned=${pinned}`);
  return parts.join('&');
}

export function buildAppPopoutUrl(state: AppPopoutState, opts: { desktop?: boolean } = {}): string {
  return opts.desktop
    ? `index.html?${buildAppPopoutQuery(state, { withFlag: true })}`
    : `${APP_POPOUT_PATH}?${buildAppPopoutQuery(state)}`;
}

export function buildPagePopoutUrl(spec: AppTabSpec, opts: { desktop?: boolean } = {}): string {
  const key = appTabKey(spec);
  return opts.desktop ? `index.html?${POPOUT_QUERY_FLAG}=page&tab=${key}` : `${PAGE_POPOUT_PATH}?tab=${key}`;
}

type Loc = { pathname: string; search: string };

function pathIs(loc: Loc, path: string): boolean {
  return loc.pathname === path || loc.pathname === `${path}/`;
}

function flag(loc: Loc): string | null {
  try {
    return new URLSearchParams(loc.search).get(POPOUT_QUERY_FLAG);
  } catch {
    return null;
  }
}

export function isAppPopoutLocation(loc: Loc): boolean {
  return pathIs(loc, APP_POPOUT_PATH) || flag(loc) === 'app';
}

export function isPagePopoutLocation(loc: Loc): boolean {
  return pathIs(loc, PAGE_POPOUT_PATH) || flag(loc) === 'page';
}

/** The first release's messaging pop-out URL (both forms). */
export function isMessagingPopoutLocation(loc: Loc): boolean {
  return pathIs(loc, POPOUT_PATH) || flag(loc) === 'messaging';
}

export function isAnyPopoutLocation(loc: Loc): boolean {
  return isAppPopoutLocation(loc) || isPagePopoutLocation(loc) || isMessagingPopoutLocation(loc);
}

/** The page a page pop-out shows, or null (missing / invalid → App shows its normal "not available" state). */
export function parsePagePopoutLocation(loc: Loc): AppTabSpec | null {
  if (!isPagePopoutLocation(loc)) return null;
  try {
    return parseAppTabKey(new URLSearchParams(loc.search).get('tab'));
  } catch {
    return null;
  }
}

export function isInAnyPopout(): boolean {
  return typeof window !== 'undefined' && isAnyPopoutLocation(window.location);
}

/** Back-compat name: true in any pop-out window or tab. */
export const isInMessagingPopout = isInAnyPopout;

/**
 * Pages that show ONE record chosen in memory (App's selectedMemberId /
 * selectedHouseholdId). A tab or pop-out only remembers `?tab=…`, so after a
 * reload such a page had no record and rendered blank. The record id now
 * travels in the tab's params (`members-view~member=123`), and a page with no
 * record falls back to its list instead of an empty screen.
 */
export const RECORD_TABS: Record<string, { param: 'member' | 'household'; list: string }> = {
  'members-view': { param: 'member', list: 'members' },
  'members-edit': { param: 'member', list: 'members' },
  'contacts-view': { param: 'member', list: 'contacts' },
  'contacts-edit': { param: 'member', list: 'contacts' },
  'household-view': { param: 'household', list: 'members' },
};

const RECORD_ID_RE = /^[1-9][0-9]{0,18}$/;

/** The record params a page should carry, given App's in-memory selection. */
export function recordParamsFor(
  tab: string,
  ids: { member?: string | number | null; household?: string | number | null },
): Record<string, string> | undefined {
  const rec = RECORD_TABS[tab];
  if (!rec) return undefined;
  const id = ids[rec.param];
  const value = id === null || id === undefined ? '' : String(id);
  return RECORD_ID_RE.test(value) ? { [rec.param]: value } : undefined;
}

/** A record id carried by a tab spec, if that page uses one and it is a valid id. */
export function recordIdFromSpec(spec: AppTabSpec | null | undefined, param: 'member' | 'household'): string | null {
  if (!spec || RECORD_TABS[spec.tab]?.param !== param) return null;
  const value = spec.params?.[param];
  return value && RECORD_ID_RE.test(value) ? value : null;
}

/** Where a record page with no record should go instead of rendering blank, or null if it has one. */
export function recordFallbackTab(
  tab: string,
  ids: { member?: string | number | null; household?: string | number | null },
): string | null {
  const rec = RECORD_TABS[tab];
  if (!rec) return null;
  const id = ids[rec.param];
  return id === null || id === undefined || id === '' ? rec.list : null;
}

/**
 * Should App's `storage` listener re-read localStorage.activeTab and switch to it?
 *
 * Two kinds of `storage` event reach that listener:
 * - in-app nudges: a component writes activeTab, then dispatches a plain
 *   `new Event('storage')` in the same window. Always followed.
 * - cross-window events: the browser fires one in every OTHER same-origin
 *   document whenever ANY localStorage key changes there.
 *
 * Following every cross-window event (whatever its key) made pages snap back:
 * with the main window's tab strip, each tab is a same-origin iframe, so
 * opening a member inside a tab made the host save its strip to localStorage,
 * the tab received that event, re-read its activeTab (still the list it opened
 * on) and jumped back to the list. In a pop-out, activeTab is private to the
 * window (lib/popoutStorageShield), so no other window's write is ever about
 * it. In the main window only a change to activeTab itself (or a clear(), key
 * null) can move the page.
 */
export function followsStorageChange(change: { crossWindow: boolean; key: string | null }, inPopout: boolean): boolean {
  if (!change.crossWindow) return true;
  if (inPopout) return false;
  return change.key === null || change.key === 'activeTab';
}

/** Adapter for a DOM event: a cross-window change is a trusted StorageEvent. */
export function shouldFollowStorageEvent(e: Event, inPopout: boolean = isInAnyPopout()): boolean {
  const crossWindow = typeof StorageEvent !== 'undefined' && e instanceof StorageEvent && e.isTrusted;
  return followsStorageChange({ crossWindow, key: crossWindow ? (e as StorageEvent).key : null }, inPopout);
}

// ---------------------------------------------------------------- messaging (first release)
// TRUS-709: 'inbox' was the 2-Way Inbox; it is kept as an accepted kind (old
// links, the desktop menu) and now opens the member Inbox, like 'members'.
export type MessagingTabKind = 'inbox' | 'members' | 'mailbox' | 'dms' | 'channels' | 'channel';

export type MessagingTabSpec =
  | { kind: 'inbox' | 'members' | 'mailbox' | 'dms' | 'channels' }
  | { kind: 'channel'; channelId: number; name?: string };

export function messagingTabKey(spec: MessagingTabSpec): string {
  return spec.kind === 'channel' ? `channel:${spec.channelId}` : spec.kind;
}

/** Parse one legacy `tabs=` entry. */
export function parseMessagingTabKey(raw: string): MessagingTabSpec | null {
  const value = raw.trim();
  if (value === 'inbox' || value === 'members' || value === 'mailbox' || value === 'dms' || value === 'channels') return { kind: value };
  const m = /^channel:(\d{1,12})$/.exec(value);
  if (m) {
    const channelId = Number(m[1]);
    if (Number.isSafeInteger(channelId) && channelId > 0) return { kind: 'channel', channelId };
  }
  return null;
}

/**
 * Every kind is a view of the Messaging workspace. The old 2-Way Inbox
 * ('inbox') opens the member Inbox (TRUS-709).
 */
export function messagingSpecToAppSpec(spec: MessagingTabSpec): AppTabSpec {
  switch (spec.kind) {
    case 'inbox':
    case 'members':
      return { tab: 'workspace-messaging', params: { view: 'members' } };
    case 'mailbox':
      return { tab: 'workspace-messaging', params: { view: 'email' } };
    case 'dms':
      return { tab: 'workspace-messaging', params: { view: 'dm-management' } };
    case 'channels':
      return { tab: 'workspace-messaging', params: { view: 'channels' } };
    case 'channel':
      return { tab: 'workspace-messaging', params: { channel: String(spec.channelId), view: 'channels' } };
  }
}

/** /popout/messaging?tabs=inbox,channel:9&active=channel:9 → the same tabs as an app pop-out. */
export function legacyMessagingSearchToAppState(search: string): AppPopoutState {
  const params = new URLSearchParams(search);
  const legacy = (params.get('tabs') || '')
    .split(',')
    .map(parseMessagingTabKey)
    .filter((t): t is MessagingTabSpec => t !== null);
  const specs = (legacy.length ? legacy : [{ kind: 'inbox' } as MessagingTabSpec]).map(messagingSpecToAppSpec);
  const activeLegacy = parseMessagingTabKey(params.get('active') || '');
  return normalizeAppPopoutState(specs, activeLegacy ? appTabKey(messagingSpecToAppSpec(activeLegacy)) : null);
}

/**
 * Rewrite a first-release messaging pop-out URL to the app pop-out in place
 * (history.replaceState, no reload). Returns true when it rewrote. Runs once
 * at module load, before App reads the location.
 */
export function migrateLegacyMessagingLocation(win: Window = window): boolean {
  const loc = win.location;
  if (!isMessagingPopoutLocation(loc)) return false;
  const state = legacyMessagingSearchToAppState(loc.search);
  const desktopForm = !pathIs(loc, POPOUT_PATH);
  const next = desktopForm
    ? `${loc.pathname}?${buildAppPopoutQuery(state, { withFlag: true })}`
    : `${APP_POPOUT_PATH}?${buildAppPopoutQuery(state)}`;
  try {
    win.history.replaceState(win.history.state, '', next);
    return true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------- in-app tabs
// A tabbed window registers this so "open in new tab" inside it adds a tab to
// its own strip instead of spawning another window.
type InAppTabHandler = (state: AppPopoutState) => void;
let inAppTabHandler: InAppTabHandler | null = null;

export function registerInAppTabHandler(fn: InAppTabHandler | null): () => void {
  inAppTabHandler = fn;
  return () => {
    if (inAppTabHandler === fn) inAppTabHandler = null;
  };
}

/** True when "open in new tab" here adds a tab to this window's own strip. */
export function hasInAppTabHandler(): boolean {
  return inAppTabHandler !== null || framedHostWindow() !== null;
}

// ---------------------------------------------------------------- main-window tab host
/**
 * The main window (the signed-in app at "/") also hosts tabs: the live page
 * plus page pop-outs in iframes (components/popout/MainWindowTabs.tsx). It sets
 * this flag on its window while its strip is mounted, so a page inside one of
 * its iframes treats it as its tab host (framedHostWindow) exactly as it would
 * a tabbed pop-out window.
 */
export const MAIN_TAB_HOST_FLAG = '__weaveinMainTabHost';

export function setMainTabHostFlag(on: boolean, win: Window = window): void {
  try {
    const w = win as unknown as Record<string, unknown>;
    if (on) w[MAIN_TAB_HOST_FLAG] = true;
    else delete w[MAIN_TAB_HOST_FLAG];
  } catch {
    /* ignore */
  }
}

function isFlaggedMainHost(win: Window): boolean {
  try {
    return (win as unknown as Record<string, unknown>)[MAIN_TAB_HOST_FLAG] === true;
  } catch {
    return false;
  }
}

export interface AddTabsEvent {
  /** Window label that should act on it; everyone else ignores it. */
  target: string;
  /** Tab keys (validated again on receipt). */
  tabs: string[];
  active: string | null;
}

// ---------------------------------------------------------------- web host registry
// Browsers give one window no way to find another it did not open. Tabbed
// hosts therefore announce themselves in localStorage (shared by every
// same-origin window) and listen on a BroadcastChannel. "Add to tab view" can
// then pick the most recently focused host synchronously — window.open must
// run inside the click, before any await, or a popup blocker eats it.
export const HOST_REGISTRY_KEY = 'weavein:popout:hosts';
export const HOST_CHANNEL = 'weavein-popout';
/** A host refreshes its entry this often; older entries are treated as gone. */
export const HOST_HEARTBEAT_MS = 5000;
const HOST_STALE_MS = HOST_HEARTBEAT_MS * 3;

export interface HostEntry {
  focusedAt: number;
  seenAt: number;
  /** The window's title, for "Move to ▸" menus. */
  title?: string;
  /** 'main' = the main window's strip (a move target, never an "Add to tab view" target). */
  kind?: 'main' | 'tabs';
}

export function readHostRegistry(storage: Storage = window.localStorage): Record<string, HostEntry> {
  try {
    const raw = storage.getItem(HOST_REGISTRY_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out: Record<string, HostEntry> = {};
    for (const [id, v] of Object.entries(parsed as Record<string, unknown>)) {
      const e = v as Partial<HostEntry> | null;
      if (/^[a-z0-9-]{1,64}$/.test(id) && e && typeof e.focusedAt === 'number' && typeof e.seenAt === 'number') {
        const entry: HostEntry = { focusedAt: e.focusedAt, seenAt: e.seenAt };
        if (typeof e.title === 'string' && e.title) entry.title = e.title.slice(0, 120);
        if (e.kind === 'main' || e.kind === 'tabs') entry.kind = e.kind;
        out[id] = entry;
      }
    }
    return out;
  } catch {
    return {};
  }
}

export function writeHostEntry(id: string, entry: HostEntry | null, storage: Storage = window.localStorage): void {
  try {
    const reg = readHostRegistry(storage);
    if (entry) reg[id] = entry;
    else delete reg[id];
    storage.setItem(HOST_REGISTRY_KEY, JSON.stringify(reg));
  } catch {
    /* private mode — "add to tab view" just opens a new window */
  }
}

/** The live tabbed pop-out host that was focused most recently, or null (the main window never counts). */
export function pickLiveHost(reg: Record<string, HostEntry>, now: number): string | null {
  let best: string | null = null;
  let bestAt = -Infinity;
  for (const [id, e] of Object.entries(reg)) {
    if (now - e.seenAt > HOST_STALE_MS || e.kind === 'main') continue;
    if (e.focusedAt > bestAt) {
      best = id;
      bestAt = e.focusedAt;
    }
  }
  return best;
}

/** Message a web host receives (BroadcastChannel or window.postMessage). */
export interface HostAddMessage {
  type: 'weavein:add-tabs';
  /** Host id it is meant for; absent when sent straight to that window. */
  target?: string;
  tabs: string[];
  active: string | null;
}

let lastOpenedHost: Window | null = null;

// ---------------------------------------------------------------- frame bridge
/** Messages a page pop-out iframe sends its tab host (same origin only). */
export type FrameMessage =
  | { type: 'weavein:page-state'; key: string; title?: string }
  | { type: 'weavein:open'; tabs: string[]; active: string | null }
  | { type: 'weavein:shortcut'; key: string };

// Mirrors lib/tabStrip.ts TAB_SHORTCUT_KEYS (kept here so this module has no import cycle).
const SHORTCUT_KEYS = new Set(['1', '2', '3', '4', '5', '6', '7', '8', '9', 'w', 'move-left', 'move-right']);

/** Shape check for a frame → host message. Anything else is ignored. */
export function parseFrameMessage(data: unknown): FrameMessage | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  switch (d.type) {
    case 'weavein:page-state': {
      if (!parseAppTabKey(d.key)) return null;
      const title = typeof d.title === 'string' ? d.title.slice(0, 120) : undefined;
      return title ? { type: d.type, key: d.key as string, title } : { type: d.type, key: d.key as string };
    }
    case 'weavein:open': {
      const state = stateFromKeys(d.tabs, d.active);
      return state.tabs.length ? { type: d.type, ...stateToKeys(state) } : null;
    }
    case 'weavein:shortcut':
      return typeof d.key === 'string' && SHORTCUT_KEYS.has(d.key) ? { type: d.type, key: d.key } : null;
    default:
      return null;
  }
}

/**
 * The tab host's window when this document is a page pop-out inside one, else
 * null. Same-origin parents only: reading a cross-origin parent's location throws.
 */
export function framedHostWindow(win: Window = window): Window | null {
  try {
    if (win.parent === win) return null;
    const parent = win.parent;
    if (parent.location.origin !== win.location.origin) return null;
    return isAppPopoutLocation(parent.location) || isFlaggedMainHost(parent) ? parent : null;
  } catch {
    return null;
  }
}

/** Host → page message: show this page in the tab (the host's sidebar was clicked). */
export interface HostNavigateMessage {
  type: 'weavein:navigate';
  /** A tab key; only its page id is acted on. */
  tab: string;
}

/**
 * The page a host asked this framed page to show, or null. Accepted only from
 * this document's own parent window, at our own origin, in the exact shape the
 * host sends — anything else (another frame, another origin, a stray message)
 * is ignored.
 */
export function parseHostNavigateMessage(e: MessageEvent, win: Window = window): AppTabSpec | null {
  try {
    if (win.parent === win) return null;
    if (e.origin !== win.location.origin) return null;
    if (e.source !== win.parent) return null;
  } catch {
    return null;
  }
  const d = e.data as { type?: unknown; tab?: unknown } | null;
  if (!d || typeof d !== 'object' || d.type !== 'weavein:navigate') return null;
  return parseAppTabKey(d.tab);
}

export function postToHost(message: FrameMessage, win: Window = window): boolean {
  const host = framedHostWindow(win);
  if (!host) return false;
  host.postMessage(message, win.location.origin);
  return true;
}

// ---------------------------------------------------------------- open
/**
 *   tab          a new browser tab (web); on desktop, a tab in the most recent
 *                tabbed window (there is no "browser tab" in a desktop app)
 *   window       a new browser popup / native window
 *   add-to-tabs  a tab in the most recent tabbed window, else a new one
 */
export type OpenMode = 'tab' | 'window' | 'add-to-tabs';

export interface OpenResult {
  ok: boolean;
  via?:
    | 'web-tab'
    | 'web-window'
    | 'web-existing-host'
    | 'desktop-window'
    | 'desktop-existing-window'
    | 'in-app-tab'
    | 'host-frame';
  /** Why not, when !ok ('popup blocked' is the one callers word specially). */
  error?: string;
}

export interface OpenDeps {
  /** Overridable for tests. */
  isDesktop?: boolean;
  now?: () => number;
  /** Overrides framedHostWindow() (tests). */
  hostWindow?: Window | null;
  /** Overrides the BroadcastChannel used to reach a web host (tests). */
  channel?: { postMessage: (m: HostAddMessage) => void } | null;
}

function errorMessage(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === 'string') return e;
  try {
    return JSON.stringify(e);
  } catch {
    return 'unknown error';
  }
}

function openChannel(): { postMessage: (m: HostAddMessage) => void; close?: () => void } | null {
  try {
    return typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(HOST_CHANNEL);
  } catch {
    return null;
  }
}

const WEB_WINDOW_FEATURES = 'popup,width=1280,height=860';

/** Open app pages somewhere other than the current page. Never throws. */
export async function openAppPopout(
  input: { tabs: AppTabSpec[]; active?: string | null },
  mode: OpenMode,
  deps: OpenDeps = {},
): Promise<OpenResult> {
  const state = normalizeAppPopoutState(input.tabs, input.active);
  if (!state.tabs.length) return { ok: false, error: 'nothing to open' };
  const isDesktop = deps.isDesktop ?? config.isDesktop;
  const now = deps.now ?? Date.now;
  const keys = stateToKeys(state);

  // Inside a tab host's iframe: tabs belong to the host's strip.
  if (mode !== 'window') {
    const host = deps.hostWindow !== undefined ? deps.hostWindow : framedHostWindow();
    if (host) {
      const msg: FrameMessage = { type: 'weavein:open', ...keys };
      host.postMessage(msg, window.location.origin);
      return { ok: true, via: 'host-frame' };
    }
    if (inAppTabHandler) {
      inAppTabHandler(state);
      return { ok: true, via: 'in-app-tab' };
    }
  }

  if (!isDesktop) {
    // Everything up to window.open stays synchronous: a popup blocker only
    // lets it through inside the click that asked for it.
    if (mode === 'tab') {
      // noopener: the new tab gets no handle on this one. window.open returns
      // null with noopener even on success, so there is nothing to check.
      window.open(buildAppPopoutUrl(state), '_blank', 'noopener');
      return { ok: true, via: 'web-tab' };
    }
    if (mode === 'add-to-tabs') {
      if (lastOpenedHost && !lastOpenedHost.closed) {
        const msg: HostAddMessage = { type: 'weavein:add-tabs', ...keys };
        lastOpenedHost.postMessage(msg, window.location.origin);
        lastOpenedHost.focus();
        return { ok: true, via: 'web-existing-host' };
      }
      const target = pickLiveHost(readHostRegistry(), now());
      const channel = deps.channel !== undefined ? deps.channel : openChannel();
      if (target && channel) {
        channel.postMessage({ type: 'weavein:add-tabs', target, ...keys });
        (channel as { close?: () => void }).close?.();
        return { ok: true, via: 'web-existing-host' };
      }
      (channel as { close?: () => void } | null)?.close?.();
    }
    const w = window.open(buildAppPopoutUrl(state), `weavein-tabs-${now()}`, WEB_WINDOW_FEATURES);
    if (!w) return { ok: false, error: 'popup blocked' };
    lastOpenedHost = w;
    return { ok: true, via: 'web-window' };
  }

  try {
    const { WebviewWindow, getAllWebviewWindows } = await import('@tauri-apps/api/webviewWindow');

    if (mode !== 'window') {
      const hosts = (await getAllWebviewWindows()).filter(
        (w) => w.label.startsWith(APP_WINDOW_LABEL_PREFIX) || w.label.startsWith(MESSAGING_WINDOW_LABEL_PREFIX),
      );
      let preferred: string | null = null;
      try {
        preferred = window.localStorage.getItem(LAST_DESKTOP_HOST_KEY);
      } catch {
        /* ignore */
      }
      const existing = hosts.find((w) => w.label === preferred) ?? hosts[hosts.length - 1];
      if (existing) {
        const { emitTo } = await import('@tauri-apps/api/event');
        const payload: AddTabsEvent = { target: existing.label, ...keys };
        await emitTo(existing.label, ADD_TABS_EVENT, payload);
        await existing.unminimize().catch(() => {});
        await existing.setFocus();
        return { ok: true, via: 'desktop-existing-window' };
      }
    }

    const label = `${APP_WINDOW_LABEL_PREFIX}${now()}`;
    const win = new WebviewWindow(label, {
      url: buildAppPopoutUrl(state, { desktop: true }),
      title: 'WeaveIn',
      width: 1280,
      height: 860,
      minWidth: 800,
      minHeight: 600,
      // macOS only; ignored on Windows, where the in-app tab strip is the tab UI.
      tabbingIdentifier: TABBING_ID,
      // The webview's own file-drop handling would swallow HTML5 drag and drop
      // on Windows, and tabs are dragged with it (lib/tabStrip.ts).
      dragDropEnabled: false,
    });
    await new Promise<void>((resolve, reject) => {
      win.once('tauri://created', () => resolve());
      win.once('tauri://error', (e) => reject(e.payload));
    });
    return { ok: true, via: 'desktop-window' };
  } catch (e) {
    console.error('openAppPopout failed', e);
    return { ok: false, error: errorMessage(e) };
  }
}

/** The messaging surfaces' entry point (Inbox, Mailbox, DMs, channels). */
export function openMessagingPopout(
  input: { tabs: MessagingTabSpec[]; active?: string | null },
  mode: OpenMode,
  deps: OpenDeps = {},
): Promise<OpenResult> {
  const tabs = input.tabs.map(messagingSpecToAppSpec);
  const activeLegacy = input.active ? parseMessagingTabKey(input.active) : null;
  const active = activeLegacy ? appTabKey(messagingSpecToAppSpec(activeLegacy)) : null;
  return openAppPopout({ tabs, active }, mode, deps);
}

/** Test hook: forget the web host this window opened. */
export function __resetPopoutForTests(): void {
  lastOpenedHost = null;
  inAppTabHandler = null;
}
